import React, { Dispatch, ReactNode, SetStateAction } from 'react'

import { cloneDeep } from 'lodash'
import { ChevronDownIcon } from 'lucide-react'

import { CommentsQuestion } from '../../manage/components/CommentsQuestion'
import { Button } from '../../../../components/ui/button'
import { AnswerItem } from './AnswerItem'

import { convertUrlsToLinks } from '../../../../helpers/convertUrlToLinks'
import { cn } from '../../../../lib/utils'

import { IQuestion } from '../../../../models/events/event.interface'
import { IAnswer } from '../../../../models/event-registered-users/event-registered-users.interface'

interface ExhibitorAnswerInputProps {
  comment?: IAnswer
  question: IQuestion
  isEditModeAnswers: boolean
  setAnswersValues: Dispatch<SetStateAction<IAnswer[]>>
  isCollapsible?: boolean
  actions?: ReactNode
  isOpen?: boolean
  setIsOpen?: (value: boolean) => void
}
export const ExhibitorAnswerInput: React.FC<ExhibitorAnswerInputProps> = ({
  comment,
  question,
  isEditModeAnswers,
  setAnswersValues,
  isCollapsible,
  actions,
  isOpen,
  setIsOpen,
}) => {
  const onChange = (name: 'files' | 'answer', value: string | string[], type?: 'remove'): void => {
    setAnswersValues((prev) => {
      let newValue: IAnswer[]

      // Check if the comment already exists in the answers
      const isExist = prev.find((item) => item.id === comment?.id)

      if (isExist) {
        newValue = prev.map((item) => {
          if (item.id === comment?.id) {
            return {
              ...item,
              answer: name === 'answer' ? (value as string) : item.answer,
              files:
                name === 'files'
                  ? type === 'remove'
                    ? (value as string[])
                    : [...item.files, value as string]
                  : item.files,
            }
          } else {
            return item
          }
        })
      } else {
        newValue = [
          ...prev,
          {
            id: `${new Date().getTime()}`,
            questionId: question.id,
            answer: name === 'answer' ? (value as string) : '',
            files: name === 'files' ? [value as string] : [],
          },
        ]
      }
      return newValue
    })
  }

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
        <h4
          className={cn(
            'flex-1 w-full md:w-auto font-medium text-SeabiscuitDark200ThemeColor text-base flex flex-wrap items-center gap-1 max-w-[800px]',
            isCollapsible && 'cursor-pointer hover:opacity-70 transition-all'
          )}
          onClick={() => setIsOpen && setIsOpen(!isOpen)}
        >
          <span dangerouslySetInnerHTML={{ __html: convertUrlsToLinks(question.value) }} />
          {question.isRequired && (
            <span className="text-SeabiscuitMainThemeColor text-[10px]">(required)</span>
          )}
        </h4>
        <div className="ml-auto flex justify-end items-center gap-4">
          {actions}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen && setIsOpen(!isOpen)}
              className={cn(
                'rounded-full duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0 hover:opacity-70 transition-all',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="pt-5">
        {isEditModeAnswers ? (
          <CommentsQuestion
            addFile={(value) => onChange('files', value)}
            files={comment?.files}
            questionId={question.id}
            defaultValue={comment?.answer}
            handleChange={(value) => {
              onChange('answer', value)
            }}
            removeFile={(_v, i) => {
              const updatedFiles = cloneDeep(comment?.files ?? [])
              updatedFiles.splice(i, 1)
              onChange('files', updatedFiles, 'remove')
            }}
          />
        ) : (
          <AnswerItem newFiles={comment?.files} value={comment?.answer} />
        )}
      </div>
    </div>
  )
}
